import type { Bounds, CollisionResult, Coordinate, Direction } from "./models"
import type { TileMapMetaProvider } from "./tile_map_meta_provider"
import { tileProfileBodyBottom } from "./tile_blocking_geometry"

/** Sweeps against tile-local footprints. Other tile families retain their legacy mover
 * unless they are obstacles in the same sweep as an authored footprint. */
export const moveAgainstTileProfiles = (
    provider: TileMapMetaProvider, bounds: Bounds, translate: Coordinate,
    result: CollisionResult, allowAdjust: boolean,
): boolean => {
    if (!provider.getTileBlockingProfile) return false
    // Composite roots can overhang their owner cell by up to one tile.
    const x1 = Math.max(0, Math.floor(Math.min(bounds.x1, bounds.x1 + translate.x) / 32) - 1)
    const x2 = Math.min(provider.width() - 1, Math.floor(Math.max(bounds.x2, bounds.x2 + translate.x) / 32) + 1)
    const y1 = Math.max(0, Math.floor(Math.min(bounds.y1, bounds.y1 + translate.y) / 32) - 1)
    const y2 = Math.min(provider.height() - 1, Math.floor(Math.max(bounds.y2, bounds.y2 + translate.y) / 32) + 1)
    const cells = []
    let hasProfile = false
    for (let x = x1; x <= x2; x++) {
        for (let y = y1; y <= y2; y++) {
            if (!provider.isCollisionAt(x, y)) continue
            const profile = provider.getTileBlockingProfile(x, y)
            if (profile) hasProfile = true
            cells.push({ x, y, profile })
        }
    }
    if (!hasProfile) return false
    result.collided = false
    const sweep = (axis: "x" | "y", delta: number) => {
        if (!delta) return
        let accepted = delta
        let hit: { x: number; y: number }
        const direction: Direction = axis === "x" ? delta > 0 ? "Right" : "Left" : delta > 0 ? "Down" : "Up"
        for (const cell of cells) {
            if (!cell.profile && !provider.isCollisionAt(cell.x, cell.y,
                bounds.centerCoordinate.x + (axis === "x" ? delta : 0),
                bounds.centerCoordinate.y + (axis === "y" ? delta : 0), direction)) continue
            for (const local of cell.profile || [{ x: 0, y: 0, width: 32, height: 32 }]) {
                const bodyBottom = tileProfileBodyBottom(bounds.y1, bounds.y2, local)
                const left = cell.x * 32 + local.x, top = cell.y * 32 + local.y
                const right = left + local.width, bottom = top + local.height
                const near = axis === "x" ? bounds.x1 : bounds.y1
                const far = axis === "x" ? bounds.x2 : bodyBottom
                const obstacleNear = axis === "x" ? left : top
                const obstacleFar = axis === "x" ? right : bottom
                if (axis === "x" ? bounds.y1 >= bottom || bodyBottom <= top : bounds.x1 >= right || bounds.x2 <= left) continue
                // Contact is legal; entry beyond it is not. Permit escape from a pre-existing overlap.
                let distance: number
                if (delta > 0) {
                    if (near >= obstacleFar) continue
                    if (far > obstacleNear && (near + far) / 2 > (obstacleNear + obstacleFar) / 2) continue
                    distance = Math.max(0, obstacleNear - far)
                    if (distance >= accepted) continue
                } else {
                    if (far <= obstacleNear) continue
                    if (near < obstacleFar && (near + far) / 2 < (obstacleNear + obstacleFar) / 2) continue
                    distance = Math.min(0, obstacleFar - near)
                    if (distance <= accepted) continue
                }
                accepted = distance
                hit = cell
            }
        }
        if (hit) {
            result.collided = true
            result.collision.x = hit.x
            result.collision.y = hit.y
            if (!allowAdjust) accepted = 0
        }
        if (axis === "x") { bounds.x1 += accepted; bounds.x2 += accepted }
        else { bounds.y1 += accepted; bounds.y2 += accepted }
    }
    sweep("x", translate.x)
    sweep("y", translate.y)
    result.adjust.x = (bounds.x1 + bounds.x2) / 2
    result.adjust.y = (bounds.y1 + bounds.y2) / 2
    const probeX = result.collided ? result.collision.x : Math.floor((translate.x > 0 ? bounds.x2 : translate.x < 0 ? bounds.x1 : result.adjust.x) / 32)
    const probeY = result.collided ? result.collision.y : Math.floor((translate.y > 0 ? bounds.y2 : translate.y < 0 ? bounds.y1 : result.adjust.y) / 32)
    const intersection = provider.intersectionAt(probeX, probeY)
    if (intersection) {
        result.intersection.x = probeX
        result.intersection.y = probeY
        result.intersection.intersectionType = intersection
    }
    return true
}
